import React, { lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
  </div>
);

// Core Pages (loaded immediately)
import LandingPage from './pages/LandingPage';
import AdminLogin from './pages/AdminLogin';

// Lazy loaded pages (loaded on demand)
const NominationForm = lazy(() => import('./pages/NominationForm'));
const SuccessPage = lazy(() => import('./pages/SuccessPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PassVerification = lazy(() => import('./pages/PassVerification'));
const PublicGallery = lazy(() => import('./pages/PublicGallery'));
const SBAPage = lazy(() => import('./pages/SBAPage'));
const CheckInScanner = lazy(() => import('./pages/CheckInScanner'));
const SponsorPortal = lazy(() => import('./pages/SponsorPortal'));
const StatusPortal = lazy(() => import('./pages/StatusPortal'));
const TrackingPortal = lazy(() => import('./pages/StatusPortal')); // Alias for tracking
const EPCLandingPage = lazy(() => import('./pages/EPCLandingPage'));
const EPCLogin = lazy(() => import('./pages/EPCLogin'));
const EPCDashboard = lazy(() => import('./pages/EPCDashboard'));
const SchoolParivarAwards = lazy(() => import('./pages/SchoolParivarAwards'));
const SPARegistration = lazy(() => import('./pages/SPARegistration'));
const SPASubmission = lazy(() => import('./pages/SPASubmission'));
const SPAGallery = lazy(() => import('./pages/SPAGallery'));
const SPATracker = lazy(() => import('./pages/SPATracker'));
const SPAGuidelines = lazy(() => import('./pages/SPAGuidelines'));
const SPALogin = lazy(() => import('./pages/SPALogin'));
const UNESCOAccreditation = lazy(() => import('./pages/UNESCOAccreditation'));
const VerifyEducatorsOnePager = lazy(() => import('./pages/VerifyEducatorsOnePager'));
const ElitePrincipalsClub = lazy(() => import('./pages/ElitePrincipalsClub'));
const EventGallery = lazy(() => import('./pages/EventGallery'));
const GalleryAdmin = lazy(() => import('./pages/GalleryAdmin'));

// EPC Mentoring (lazy loaded)
const MentorBrowse = lazy(() => import('./pages/MentorBrowse'));
const MentorProfile = lazy(() => import('./pages/MentorProfile'));
const MentorApply = lazy(() => import('./pages/MentorApply'));
const MenteeRegister = lazy(() => import('./pages/MenteeRegister'));
const MentorLogin = lazy(() => import('./pages/MentorLogin'));
const MentorDashboard = lazy(() => import('./pages/MentorDashboard'));
const MentorEarnings = lazy(() => import('./pages/MentorEarnings'));
const PaymentConfirmation = lazy(() => import('./pages/PaymentConfirmation'));
const WhatsAppDashboard = lazy(() => import('./pages/WhatsAppDashboard'));
const GmailDashboard = lazy(() => import('./pages/GmailDashboard'));
const SecretaryAdmin = lazy(() => import('./pages/SecretaryAdmin'));
const SchoolSurvey = lazy(() => import('./pages/SchoolSurvey'));
const SurveyDashboard = lazy(() => import('./pages/SurveyDashboard'));
const SurveyReport = lazy(() => import('./pages/SurveyReport'));

// Context providers (needed immediately)
import { AuthProvider } from './context/AuthContext';
import { CenterAuthProvider } from './context/CenterAuthContext';
import { AssessmentProvider } from './context/AssessmentContext';

// Educator Evaluation Module (lazy loaded)
const EducatorLogin = lazy(() => import('./components/educators/EducatorLogin'));
const EducatorDashboard = lazy(() => import('./components/educators/EducatorDashboard'));
const CenterLogin = lazy(() => import('./components/center/CenterLogin'));
const CenterDashboard = lazy(() => import('./components/center/CenterDashboard'));
const Scheduling = lazy(() => import('./components/center/Scheduling'));
const MasterAssessors = lazy(() => import('./components/center/MasterAssessors'));
const Revenue = lazy(() => import('./components/center/Revenue'));
const QualityTracker = lazy(() => import('./components/center/QualityTracker'));
const Compliance = lazy(() => import('./components/center/Compliance'));

// Assessment Module (lazy loaded)
const AssessmentWelcome = lazy(() => import('./pages/AssessmentWelcome'));
const AssessmentStage1A = lazy(() => import('./pages/AssessmentStage1A'));
const AssessmentGateCheck = lazy(() => import('./pages/AssessmentGateCheck'));
const AssessmentStage1B = lazy(() => import('./pages/AssessmentStage1B'));
const AssessmentResults = lazy(() => import('./pages/AssessmentResults'));

// PAN (Principal Advisory Network) Module - lazy loaded individually
const PANLanding = lazy(() => import('./pages/pan/PANLanding'));
const SeekerRegister = lazy(() => import('./pages/pan/SeekerRegister'));
const AdvisorInvite = lazy(() => import('./pages/pan/AdvisorInvite'));
const PANLogin = lazy(() => import('./pages/pan/PANLogin'));
const AdvisorDashboard = lazy(() => import('./pages/pan/AdvisorDashboard'));
const SeekerDashboard = lazy(() => import('./pages/pan/SeekerDashboard'));
const PANAdmin = lazy(() => import('./pages/pan/PANAdmin'));

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <AuthProvider>
        <CenterAuthProvider>
          <AssessmentProvider>
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* SBA Page (Skill Assessment) is the home page */}
              <Route path="/" element={<SBAPage />} />
              {/* Dark awards page moved to /skill-bharat-samman-5 */}
              <Route path="/skill-bharat-samman-5" element={<LandingPage />} />
              <Route path="/sbs5" element={<LandingPage />} />
              {/* Keep /sba as alias for backward compatibility */}
              <Route path="/sba" element={<SBAPage />} />
              <Route path="/epc" element={<EPCLandingPage />} />
              <Route path="/epc/login" element={<EPCLogin />} />
              <Route path="/epc/dashboard" element={<EPCDashboard />} />
              
              {/* EPC Mentoring Routes */}
              <Route path="/mentoring" element={<MentorBrowse />} />
              <Route path="/mentoring/mentor/login" element={<MentorLogin />} />
              <Route path="/mentoring/dashboard" element={<MentorDashboard />} />
              <Route path="/mentoring/mentor/earnings" element={<MentorEarnings />} />
              <Route path="/mentoring/mentor/:mentorId" element={<MentorProfile />} />
              <Route path="/mentoring/mentor/apply" element={<MentorApply />} />
              <Route path="/mentoring/mentee/register" element={<MenteeRegister />} />
              <Route path="/mentoring/payment-confirmation" element={<PaymentConfirmation />} />
              
              <Route path="/school-parivar-awards" element={<SchoolParivarAwards />} />
              <Route path="/school-parivar-awards/register" element={<SPARegistration />} />
              <Route path="/school-parivar-awards/submit" element={<SPASubmission />} />
              <Route path="/school-parivar-awards/gallery" element={<SPAGallery />} />
              <Route path="/school-parivar-awards/track/:registrationId" element={<SPATracker />} />
              <Route path="/school-parivar-awards/guidelines" element={<SPAGuidelines />} />
              <Route path="/spa" element={<SchoolParivarAwards />} />
              <Route path="/spa/register" element={<SPARegistration />} />
              <Route path="/spa/login" element={<SPALogin />} />
              <Route path="/spa/track/:registrationId" element={<SPATracker />} />
              <Route path="/spa/gallery" element={<SPAGallery />} />
              <Route path="/spa/guidelines" element={<SPAGuidelines />} />
              <Route path="/unesco" element={<UNESCOAccreditation />} />
              <Route path="/unesco-accreditation" element={<UNESCOAccreditation />} />
              <Route path="/elite-principals-club" element={<ElitePrincipalsClub />} />
              <Route path="/epc-membership" element={<ElitePrincipalsClub />} />
              <Route path="/sponsors" element={<SponsorPortal />} />
              <Route path="/nominate/:type" element={<NominationForm />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/secretary" element={<SecretaryAdmin />} />
              <Route path="/verify/:nomineeId" element={<PassVerification />} />
              <Route path="/gallery" element={<PublicGallery />} />
              <Route path="/event-gallery" element={<EventGallery />} />
              <Route path="/admin/gallery" element={<GalleryAdmin />} />
              <Route path="/checkin" element={<CheckInScanner />} />
              <Route path="/whatsapp" element={<WhatsAppDashboard />} />
              <Route path="/gmail" element={<GmailDashboard />} />
              <Route path="/status" element={<StatusPortal />} />
              <Route path="/track" element={<StatusPortal />} />
              <Route path="/check-status" element={<StatusPortal />} />
              
              {/* Educators Evaluation Module Routes */}
              <Route path="/evaluate" element={<VerifyEducatorsOnePager />} />
              <Route path="/evaluate/educator/login" element={<EducatorLogin />} />
              <Route path="/evaluate/educator/dashboard" element={<EducatorDashboard />} />
              <Route path="/evaluate/educator/assessments" element={<EducatorDashboard />} />
              <Route path="/evaluate/educator/certificates" element={<EducatorDashboard />} />
              <Route path="/evaluate/educator/schedule" element={<EducatorDashboard />} />
              <Route path="/evaluate/educator/profile" element={<EducatorDashboard />} />
              <Route path="/evaluate/center/login" element={<CenterLogin />} />
              <Route path="/evaluate/center/dashboard" element={<CenterDashboard />} />
              <Route path="/evaluate/center/scheduling" element={<Scheduling />} />
              <Route path="/evaluate/center/master-assessors" element={<MasterAssessors />} />
              <Route path="/evaluate/center/revenue" element={<Revenue />} />
              <Route path="/evaluate/center/quality-tracker" element={<QualityTracker />} />
              <Route path="/evaluate/center/compliance" element={<Compliance />} />
              
              {/* Assessment Module Routes */}
              <Route path="/assessment" element={<AssessmentWelcome />} />
              <Route path="/assessment/stage-1a" element={<AssessmentStage1A />} />
              <Route path="/assessment/gate-check" element={<AssessmentGateCheck />} />
              <Route path="/assessment/stage-1b" element={<AssessmentStage1B />} />
              <Route path="/assessment/results" element={<AssessmentResults />} />
              
              {/* Principal Advisory Network (PAN) Routes */}
              <Route path="/pan" element={<PANLanding />} />
              <Route path="/pan/login" element={<PANLogin />} />
              <Route path="/pan/seeker/register" element={<SeekerRegister />} />
              <Route path="/pan/invite/:code" element={<AdvisorInvite />} />
              <Route path="/pan/advisor/dashboard" element={<AdvisorDashboard />} />
              <Route path="/pan/seeker/dashboard" element={<SeekerDashboard />} />
              <Route path="/pan/admin" element={<PANAdmin />} />
              
              {/* School Survey - Parent Research */}
              <Route path="/schoolsurvey1" element={<SchoolSurvey />} />
              <Route path="/survey" element={<SchoolSurvey />} />
              <Route path="/survey/report" element={<SurveyReport />} />
              <Route path="/survey/admin" element={<SurveyDashboard />} />
              <Route path="/admin/survey" element={<SurveyDashboard />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </AssessmentProvider>
        </CenterAuthProvider>
      </AuthProvider>
      <Toaster />
    </div>
  );
}

export default App;
